// @ts-strict-ignore
import { useUser } from "@dashboard/auth";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { ListViews } from "@dashboard/types";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getSortParams } from "@dashboard/utils/sort";
import React from "react";
import { useIntl } from "react-intl";

import QuotationListPage from "../../components/QuotationListPage/QuotationListPage";
import {
  quotationListUrl,
  QuotationListUrlQueryParams,
  // QuotationListUrlSortField,
} from "../../urls";
import { useGetVendorQuotesQuery } from "@dashboard/graphql";
// import { DEFAULT_SORT_KEY } from "./sort";
import { PAGINATE_BY } from "@dashboard/config";

interface QuotationListProps {
  params: QuotationListUrlQueryParams;
}

export const QuotationList = ({ params }: QuotationListProps) => {
  const navigate = useNavigator();
  const { updateListSettings, settings } = useListSettings(ListViews.QUOTATION_LIST);
  const { user } = useUser();
  const intl = useIntl();

  usePaginationReset(quotationListUrl, params, settings?.rowNumber);

  // Use PAGINATE_BY as fallback if settings?.rowNumber is undefined
  const paginationState = createPaginationState(settings?.rowNumber ?? PAGINATE_BY, params);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      // status: "PENDING",
      tenantId: user?.tenantId || null,
      ...(user?.isVendor === false && { sendToEvolve: true }),
    }),
    [paginationState, user?.tenantId, user?.isVendor],
  );

  const { data, loading } = useGetVendorQuotesQuery({
    displayLoader: true,
    variables: queryVariables,
  });

  const paginationValues = usePaginator({
    pageInfo: data?.quotations?.pageInfo,
    paginationState,
    queryString: params,
  });

  const handleSort = createSortHandler(navigate, quotationListUrl, params);

  const handleSearchChange = (query: string) => {
    navigate(
      quotationListUrl({
        ...params,
        query,
      }),
    );
  };

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <QuotationListPage
        settings={settings}
        disabled={loading}
        quotations={mapEdgesToItems(data?.quotations)}
        sort={getSortParams(params)}
        onUpdateListSettings={updateListSettings}
        onSort={handleSort}
        onSearchChange={handleSearchChange}
        initialSearch={params.query || ""}
        params={params}
      />
    </PaginatorContext.Provider>
  );
};

export default QuotationList;
